import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    Offcanvas,
    OffcanvasHeader,
    OffcanvasBody,
    Input,
    InputGroup,
    Badge,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ListGroup,
    ListGroupItem,
    Spinner,
} from "reactstrap";
import SimpleBar from "simplebar-react";
import axios from "axios";
import { useAuth } from "../../Provider/AuthProvider";

interface Message {
    id: number;
    text: string;
    sender: string;
    sender_name?: string;
    timestamp: Date;
    isOwn: boolean;
    isRead: boolean;
    datetime?: string;
    time?: string;
    date?: string;
}

interface Conversation {
    id: number;
    name: string;
    avatar?: string;
    lastMessage: string;
    lastMessageTime: string;
    lastMessageTimestamp?: number;
    unreadCount: number;
    online: boolean;
    messages: Message[];
    contact_id?: number;
}

interface ChatUser {
    id: number;
    name: string;
    nickname: string;
    email: string;
    profile?: string;
    messagecount?: number;
    status?: UserStatus;
}

interface UserStatus {
    is_online: boolean;
    status_text: string;
    status_code: string;
    last_action: string | null;
    last_action_formatted: string;
    action_id: number | null;
    action_type: string | null;
}

interface ChatStatutResponse {
    success: boolean;
    message: string;
    data: {
        chat_users: ChatUser[];
        entity: number;
        current_time: string;
        offline_threshold_minutes: number;
    };
}

interface UnreadMessage {
    id: number;
    from_id: number;
    to_id: number;
    from_name: string;
    to_name: string;
    from_login: string;
    to_login: string;
    from_photo: string | null;
    to_photo: string | null;
    msg: string;
    datetime: string;
    time: string;
    date: string;
    relative_time: string;
    isRead: boolean;
}

interface UnreadBySender {
    from_id: number;
    from_name: string;
    from_login: string;
    from_photo: string | null;
    count: number;
    latest_message: UnreadMessage;
}

interface ChatSidebarProps {
    onUnreadCountChange?: (count: number) => void;
}

const API_URL = 'https://api.apapiecesauto.fr/apiV2/';

// Fonction utilitaire pour formater la date/heure
const formatMessageTime = (datetime: string | Date): string => {
    const messageDate = new Date(datetime);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const isToday = messageDate.toDateString() === today.toDateString();
    const isYesterday = messageDate.toDateString() === yesterday.toDateString();
    
    if (isToday) {
        // Aujourd'hui : afficher l'heure
        return messageDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (isYesterday) {
        // Hier : afficher "Hier"
        return `Hier ${messageDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
        // Autres jours : afficher la date
        return messageDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
};

// Fonction pour obtenir l'affichage du dernier message dans la liste
const formatLastMessageTime = (datetime: string | Date): string => {
    const messageDate = new Date(datetime);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const isToday = messageDate.toDateString() === today.toDateString();
    const isYesterday = messageDate.toDateString() === yesterday.toDateString();
    
    if (isToday) {
        return messageDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (isYesterday) {
        return 'Hier';
    } else {
        return messageDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    }
};

// Fonction pour regrouper les messages par date
const groupMessagesByDate = (messages: Message[]): { date: string; messages: Message[] }[] => {
    const groups: { [key: string]: Message[] } = {};
    
    messages.forEach(message => {
        const messageDate = new Date(message.timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        let dateKey: string;
        let displayDate: string;
        
        if (messageDate.toDateString() === today.toDateString()) {
            dateKey = 'today';
            displayDate = "Aujourd'hui";
        } else if (messageDate.toDateString() === yesterday.toDateString()) {
            dateKey = 'yesterday';
            displayDate = 'Hier';
        } else {
            dateKey = messageDate.toLocaleDateString('fr-FR');
            displayDate = messageDate.toLocaleDateString('fr-FR', { 
                weekday: 'long', 
                day: '2-digit', 
                month: 'long', 
                year: 'numeric' 
            });
        }
        
        if (!groups[dateKey]) {
            groups[dateKey] = {
                messages: [],
                displayDate: displayDate
            } as any;
        }
        (groups[dateKey] as any).messages.push(message);
        (groups[dateKey] as any).displayDate = displayDate;
    });
    
    return Object.entries(groups).map(([key, group]: [string, any]) => ({
        date: group.displayDate,
        messages: group.messages
    }));
};

const StatusIndicator: React.FC<{ status?: UserStatus; isLoading?: boolean }> = ({ status, isLoading }) => {
    if (isLoading) {
        return <Spinner size="sm" color="secondary" style={{ width: '10px', height: '10px' }} />;
    }

    if (!status) {
        return <i className="ri-question-line text-secondary" style={{ fontSize: '12px' }}></i>;
    }

    let color = '';
    let title = '';
    let blinkClass = '';
    let statusLabel = '';

    if (status.is_online || status.status_code === 'online') {
        color = '#10b981';
        blinkClass = 'blink-green';
        statusLabel = 'En ligne';
        title = `🟢 En ligne • Actif ${status.last_action_formatted || 'maintenant'}`;
    } else if (status.status_code === 'away') {
        color = '#f59e0b';
        statusLabel = 'Absent';
        title = `🟠 Absent • Dernière activité : ${status.last_action_formatted || 'récemment'}`;
    } else {
        color = '#ef4444';
        statusLabel = 'Hors ligne';
        
        if (!status.last_action_formatted || status.last_action_formatted === '') {
            title = `🔴 Hors ligne • Jamais connecté ou inactif depuis longtemps`;
        } else if (status.last_action_formatted.includes('jour') || status.last_action_formatted.includes('jours')) {
            title = `🔴 Hors ligne • Dernière connexion : ${status.last_action_formatted}`;
        } else if (status.last_action_formatted.match(/\d+h/)) {
            title = `🔴 Hors ligne • Dernière connexion : ${status.last_action_formatted}`;
        } else {
            title = `🔴 Hors ligne • Dernière activité : ${status.last_action_formatted}`;
        }
    }

    return (
        <div
            className={`status-dot ${blinkClass}`}
            style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: color,
                display: 'inline-block',
                marginLeft: '8px',
                cursor: 'help',
                boxShadow: status.is_online ? '0 0 4px rgba(16, 185, 129, 0.8)' : 'none',
                transition: 'all 0.3s ease'
            }}
            title={title}
        />
    );
};

const ChatSidebar = ({ onUnreadCountChange }: ChatSidebarProps) => {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
    const [newMessage, setNewMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [users, setUsers] = useState<ChatUser[]>([]);
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersSearchTerm, setUsersSearchTerm] = useState('');
    const [totalUnreadCount, setTotalUnreadCount] = useState(0);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [isSendingOrReceiving, setIsSendingOrReceiving] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const unreadPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const statusPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const currentContactIdRef = useRef<number | null>(null);
    const isRefreshingUnreadRef = useRef(false);
    const lastMessageIdRef = useRef<number | null>(null);
    const lastUnreadFetchRef = useRef<number>(0);

    const userinfo = user?.userinfo;
    const entity = user?.entity || 3;

    const apiClient = axios.create({
        baseURL: API_URL,
        headers: {
            'Content-Type': 'application/json',
        }
    });

    apiClient.interceptors.request.use(
        (config) => {
            const token = user?.token || userinfo?.token;
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    useEffect(() => {
        if (selectedConversation?.messages?.length) {
            scrollToBottom();
        }
    }, [selectedConversation?.messages?.length]);

    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (isSendingOrReceiving) return;

            const target = event.target as HTMLElement;
            const offcanvasElement = document.getElementById('theme-settings-offcanvas');

            if (offcanvasElement && !offcanvasElement.contains(target)) {
                const isToggleButton = target.closest('.btn-ghost-secondary');
                const isBackButton = target.closest('.back-button');
                const isConversationItem = target.closest('.conversation-item');
                const isMessageInput = target.closest('textarea');
                const isSendButton = target.closest('.btn-primary');
                const isModalBackdrop = target.closest('.modal-backdrop');
                const isModal = target.closest('.modal');

                if (!isToggleButton && !isBackButton && !isConversationItem &&
                    !isMessageInput && !isSendButton && !isModalBackdrop && !isModal) {
                    setOpen(false);
                }
            }
        };

        const timer = setTimeout(() => {
            document.addEventListener('click', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [open, isSendingOrReceiving]);

    const fetchChatUsersWithStatus = useCallback(async () => {
        if (!userinfo?.id) return;

        setUsersLoading(true);
        try {
            const response: any = await apiClient.post('/', {
                action: 'getChatStatut',
                user_id: userinfo.id,
                entity: entity
            });

            if (response.data?.success && response.data?.data?.chat_users) {
                const filteredUsers: ChatUser[] = response.data.data.chat_users.filter(
                    (u: ChatUser) => u.id !== userinfo.id
                );
                setUsers(filteredUsers);
            }
        } catch (error: any) {
            console.error('Erreur lors de la récupération des utilisateurs:', error);
        } finally {
            setUsersLoading(false);
        }
    }, [userinfo?.id, entity, apiClient]);

    const refreshUsersStatus = useCallback(async () => {
        if (!showUsersModal && !open) return;
        if (!userinfo?.id) return;

        try {
            const response: any = await apiClient.post('/', {
                action: 'getChatStatut',
                user_id: userinfo.id,
                entity: entity
            });

            if (response.data?.success && response.data?.data?.chat_users) {
                const updatedUsers: ChatUser[] = response.data.data.chat_users.filter(
                    (u: ChatUser) => u.id !== userinfo.id
                );

                setUsers(prevUsers => {
                    const updatedUsersMap = new Map<number, ChatUser>(updatedUsers.map((u: ChatUser) => [u.id, u]));
                    return prevUsers.map(user => {
                        const updated = updatedUsersMap.get(user.id);
                        if (updated && updated.status && JSON.stringify(updated.status) !== JSON.stringify(user.status)) {
                            return { ...user, status: updated.status };
                        }
                        return user;
                    });
                });
            }
        } catch (error: any) {
            console.error('Erreur lors du rafraîchissement des statuts:', error);
        }
    }, [userinfo?.id, entity, apiClient, showUsersModal, open]);

    useEffect(() => {
        if ((showUsersModal || open) && users.length > 0) {
            statusPollingIntervalRef.current = setInterval(() => {
                refreshUsersStatus();
            }, 30000);

            return () => {
                if (statusPollingIntervalRef.current) {
                    clearInterval(statusPollingIntervalRef.current);
                    statusPollingIntervalRef.current = null;
                }
            };
        }
    }, [showUsersModal, open, users.length, refreshUsersStatus]);

    const fetchUnreadMessages = useCallback(async (force = false) => {
        if (!userinfo?.id) return 0;
        if (isRefreshingUnreadRef.current) return 0;

        const now = Date.now();
        if (!force && now - lastUnreadFetchRef.current < 30_000) {
            return 0;
        }

        isRefreshingUnreadRef.current = true;
        lastUnreadFetchRef.current = now;

        try {
            const response: any = await apiClient.post('/', {
                action: 'getUnreadMessages',
                user_id: userinfo.id,
                entity: entity
            });

            if (response.data?.success && response.data?.data) {
                const totalUnread = response.data.data.total_unread || 0;
                const unreadBySender = response.data.data.unread_by_sender || [];

                if (totalUnread !== totalUnreadCount) {
                    setTotalUnreadCount(totalUnread);
                    if (onUnreadCountChange) {
                        onUnreadCountChange(totalUnread);
                    }
                }

                if (!selectedConversation) {
                    setConversations(prevConversations => {
                        const updatedConversations = [...prevConversations];

                        unreadBySender.forEach((sender: UnreadBySender) => {
                            const conversationIndex = updatedConversations.findIndex(
                                conv => conv.id === sender.from_id
                            );

                            if (conversationIndex !== -1) {
                                updatedConversations[conversationIndex] = {
                                    ...updatedConversations[conversationIndex],
                                    unreadCount: sender.count,
                                    lastMessage: sender.latest_message.msg,
                                    lastMessageTime: formatLastMessageTime(sender.latest_message.datetime),
                                    lastMessageTimestamp: new Date(sender.latest_message.datetime).getTime(),
                                };
                            } else {
                                updatedConversations.unshift({
                                    id: sender.from_id,
                                    contact_id: sender.from_id,
                                    name: sender.from_name,
                                    avatar: sender.from_photo || undefined,
                                    lastMessage: sender.latest_message.msg,
                                    lastMessageTime: formatLastMessageTime(sender.latest_message.datetime),
                                    lastMessageTimestamp: new Date(sender.latest_message.datetime).getTime(),
                                    unreadCount: sender.count,
                                    online: false,
                                    messages: []
                                });
                            }
                        });

                        return updatedConversations;
                    });
                }

                return totalUnread;
            }
        } catch (error: any) {
            console.error('Erreur lors de la récupération des messages non lus:', error);
        } finally {
            isRefreshingUnreadRef.current = false;
        }

        return 0;
    }, [userinfo?.id, entity, apiClient, onUnreadCountChange, totalUnreadCount, selectedConversation]);

    useEffect(() => {
        if (userinfo?.id && user?.token) {
            fetchUnreadMessages(true);

            unreadPollingIntervalRef.current = setInterval(() => {
                fetchUnreadMessages(true);
            }, 30_000);
        }

        return () => {
            if (unreadPollingIntervalRef.current) {
                clearInterval(unreadPollingIntervalRef.current);
                unreadPollingIntervalRef.current = null;
            }
        };
    }, [userinfo?.id, user?.token]);
    
    const markMessagesAsRead = useCallback(async (fromId: number) => {
        if (!userinfo?.id) return;

        try {
            await apiClient.post('/', {
                action: 'markAsRead',
                user_id: userinfo.id,
                from_id: fromId,
                entity: entity
            });
            setTotalUnreadCount(prev => {
                const updated = Math.max(0, prev - 1);
                if (onUnreadCountChange) onUnreadCountChange(updated);
                return updated;
            });
            setConversations(prev =>
                prev.map(conv =>
                    conv.id === fromId ? { ...conv, unreadCount: 0 } : conv
                )
            );
        } catch (error: any) {
            console.error('Erreur lors du marquage des messages comme lus:', error);
        }
    }, [userinfo?.id, entity, apiClient, onUnreadCountChange]);

    const fetchConversations = useCallback(async () => {
        if (!userinfo?.id) return;

        setLoading(true);
        try {
            const response: any = await apiClient.post('/', {
                action: 'getAllMessages',
                user_id: userinfo.id,
                entity: entity
            });

            if (response.data?.success && response.data?.data?.conversations) {
                const formattedConversations: Conversation[] = response.data.data.conversations.map((conv: any) => ({
                    id: conv.contact.id,
                    contact_id: conv.contact.id,
                    name: conv.contact.name || conv.contact.login,
                    avatar: conv.contact.photo,
                    lastMessage: conv.last_message?.msg || 'Aucun message',
                    lastMessageTime: conv.last_message ? formatLastMessageTime(conv.last_message.datetime) : '',
                    lastMessageTimestamp: conv.last_message_timestamp,
                    unreadCount: 0,
                    online: false,
                    messages: []
                }));

                setConversations(formattedConversations);
                fetchUnreadMessages();
            }
        } catch (error: any) {
            console.error('Erreur lors de la récupération des conversations:', error);
        } finally {
            setLoading(false);
        }
    }, [userinfo?.id, entity, apiClient, fetchUnreadMessages]);

    const fetchMessages = useCallback(async (contactId: number): Promise<Message[]> => {
        if (!userinfo?.id) return [];

        try {
            const response: any = await apiClient.post('/', {
                action: 'getChatMessage',
                user_id: userinfo.id,
                contact_id: contactId,
                entity: entity
            });

            if (response.data?.success && response.data?.data?.chats) {
                const messages = response.data.data.chats.map((msg: any) => ({
                    id: msg.id,
                    text: msg.msg,
                    sender: msg.is_from_current_user ? "Moi" : (msg.from_name || msg.to_name),
                    sender_name: msg.from_name,
                    timestamp: new Date(msg.datetime),
                    isOwn: msg.is_from_current_user || msg.message_type === 'sent',
                    isRead: msg.isRead || false,
                    datetime: msg.datetime,
                    time: msg.time,
                    date: msg.date
                }));

                if (messages.length > 0) {
                    lastMessageIdRef.current = messages[messages.length - 1].id;
                }

                return messages;
            }
        } catch (error: any) {
            console.error('Erreur lors de la récupération des messages:', error);
        }

        return [];
    }, [userinfo?.id, entity, apiClient]);

    const sendMessageToApi = useCallback(async (message: string, toId: number) => {
        if (!userinfo?.id || !message.trim()) return null;

        setSendingMessage(true);
        try {
            const response: any = await apiClient.post('/', {
                action: 'sendMessage',
                entity: entity,
                userid: userinfo.id,
                to_id: toId,
                message: message.trim()
            });

            return response.data;
        } catch (error: any) {
            console.error('Erreur lors de l\'envoi du message:', error);
            return null;
        } finally {
            setSendingMessage(false);
        }
    }, [userinfo?.id, entity, apiClient]);

    const openConversation = useCallback(async (conversation: Conversation, event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation();
        }

        setIsSendingOrReceiving(true);

        const contactId = conversation.contact_id || conversation.id;

        if (currentContactIdRef.current === contactId && selectedConversation) {
            setIsSendingOrReceiving(false);
            return;
        }

        currentContactIdRef.current = contactId;
        lastMessageIdRef.current = null;
        setLoadingMessages(true);

        const messages = await fetchMessages(contactId);

        const newSelectedConv = {
            ...conversation,
            messages: messages,
            unreadCount: 0
        };

        setSelectedConversation(newSelectedConv);

        setConversations(prev =>
            prev.map(conv =>
                conv.id === conversation.id
                    ? { ...conv, unreadCount: 0, messages: messages }
                    : conv
            )
        );

        await markMessagesAsRead(contactId).catch(console.error);

        setLoadingMessages(false);
        scrollToBottom();

        setTimeout(() => {
            setIsSendingOrReceiving(false);
        }, 300);

    }, [fetchMessages, markMessagesAsRead, selectedConversation]);

    const startNewConversation = useCallback(async (chatUser: ChatUser) => {
        const existingConversation = conversations.find(conv => conv.id === chatUser.id);

        if (existingConversation) {
            await openConversation(existingConversation);
        } else {
            const newConversation: Conversation = {
                id: chatUser.id,
                contact_id: chatUser.id,
                name: chatUser.name,
                avatar: chatUser.profile,
                lastMessage: "Nouvelle conversation",
                lastMessageTime: "Maintenant",
                unreadCount: 0,
                online: chatUser.status?.is_online || false,
                messages: []
            };

            setConversations(prev => [newConversation, ...prev]);
            setSelectedConversation(newConversation);
            currentContactIdRef.current = chatUser.id;
        }

        setShowUsersModal(false);
    }, [conversations, openConversation]);

    const sendMessage = useCallback(async () => {
        if (newMessage.trim() === '' || !selectedConversation || sendingMessage) return;

        const toId = selectedConversation.contact_id || selectedConversation.id;
        const messageText = newMessage.trim();

        setIsSendingOrReceiving(true);

        const tempMessage: Message = {
            id: Date.now(),
            text: messageText,
            sender: "Moi",
            timestamp: new Date(),
            isOwn: true,
            isRead: false,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setSelectedConversation(prev => {
            if (!prev) return null;
            return {
                ...prev,
                messages: [...prev.messages, tempMessage],
                lastMessage: messageText,
                lastMessageTime: formatMessageTime(new Date()),
            };
        });

        setConversations(prev =>
            prev.map(conv =>
                conv.id === selectedConversation.id
                    ? {
                        ...conv,
                        lastMessage: messageText,
                        lastMessageTime: formatMessageTime(new Date()),
                    }
                    : conv
            )
        );

        setNewMessage('');
        scrollToBottom();

        const result = await sendMessageToApi(messageText, toId);

        if (result?.success && result?.data?.message) {
            const realMessage: Message = {
                id: result.data.message.id,
                text: result.data.message.msg,
                sender: "Moi",
                sender_name: result.data.message.from_name,
                timestamp: new Date(result.data.message.datetime),
                isOwn: true,
                isRead: result.data.message.isRead,
                datetime: result.data.message.datetime,
                time: result.data.message.time,
                date: result.data.message.date
            };

            setSelectedConversation(prev => {
                if (!prev) return null;
                const updatedMessagesList = prev.messages.map(msg =>
                    msg.id === tempMessage.id ? realMessage : msg
                );
                return {
                    ...prev,
                    messages: updatedMessagesList,
                };
            });

            setConversations(prevConversations => {
                return prevConversations.map(conv => {
                    if (conv.id === selectedConversation.id) {
                        const updatedConvMessages = conv.messages.map(msg =>
                            msg.id === tempMessage.id ? realMessage : msg
                        );
                        return { ...conv, messages: updatedConvMessages };
                    }
                    return conv;
                });
            });

            lastMessageIdRef.current = realMessage.id;
        }

        setTimeout(() => {
            setIsSendingOrReceiving(false);
        }, 300);

    }, [newMessage, selectedConversation, sendingMessage, sendMessageToApi]);

    const backToList = useCallback(() => {
        setSelectedConversation(null);
        currentContactIdRef.current = null;
        lastMessageIdRef.current = null;
        fetchConversations();
    }, [fetchConversations]);

    const openUsersModal = useCallback(() => {
        setShowUsersModal(true);
        fetchChatUsersWithStatus();
    }, [fetchChatUsersWithStatus]);

    const checkForNewMessages = useCallback(async () => {
        if (!open || !selectedConversation) {
            return;
        }

        const contactId = selectedConversation.contact_id || selectedConversation.id;

        if (currentContactIdRef.current !== contactId) {
            return;
        }

        try {
            const allMessages = await fetchMessages(contactId);

            if (!allMessages || allMessages.length === 0) {
                return;
            }

            const currentMessages = selectedConversation.messages || [];
            const currentMessageIds = new Set(currentMessages.map(m => m.id));
            const newMessages = allMessages.filter(m => !currentMessageIds.has(m.id));

            if (newMessages.length === 0) {
                return;
            }

            const receivedMessages = newMessages.filter(msg => !msg.isOwn);

            if (receivedMessages.length === 0) {
                return;
            }

            setIsSendingOrReceiving(true);

            setSelectedConversation(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    messages: [...prev.messages, ...receivedMessages],
                    lastMessage: receivedMessages[receivedMessages.length - 1].text,
                    lastMessageTime: formatMessageTime(receivedMessages[receivedMessages.length - 1].timestamp),
                };
            });

            setConversations(prevConversations => {
                return prevConversations.map(conv => {
                    if (conv.id === contactId) {
                        const updatedMessages = [...(conv.messages || []), ...receivedMessages];
                        return {
                            ...conv,
                            messages: updatedMessages,
                            lastMessage: receivedMessages[receivedMessages.length - 1].text,
                            lastMessageTime: formatMessageTime(receivedMessages[receivedMessages.length - 1].timestamp),
                        };
                    }
                    return conv;
                });
            });

            if (currentContactIdRef.current === contactId) {
                await markMessagesAsRead(contactId);
            }

            scrollToBottom();

            setTimeout(() => {
                setIsSendingOrReceiving(false);
            }, 300);

        } catch (error) {
            console.error('Erreur lors de la vérification des nouveaux messages:', error);
            setIsSendingOrReceiving(false);
        }
    }, [open, selectedConversation, fetchMessages, markMessagesAsRead]);

    useEffect(() => {
        if (open && selectedConversation && userinfo?.id) {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }

            checkForNewMessages();

            pollingIntervalRef.current = setInterval(() => {
                checkForNewMessages();
            }, 2000);
        } else if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [open, selectedConversation, userinfo?.id, checkForNewMessages]);

    useEffect(() => {
        if (userinfo?.id && (user?.token || userinfo?.token)) {
            fetchConversations();
            fetchChatUsersWithStatus();
        }
    }, [userinfo?.id, user?.token, userinfo?.token]);

    const filteredConversations = conversations.filter(conv =>
        conv.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(usersSearchTerm.toLowerCase()) ||
        (user.nickname && user.nickname.toLowerCase().includes(usersSearchTerm.toLowerCase()))
    );

    // Rendu des messages avec regroupement par date
    const renderMessagesWithDates = () => {
        if (!selectedConversation?.messages?.length) return null;
        
        const groupedMessages = groupMessagesByDate(selectedConversation.messages);
        
        return (
            <>
                {groupedMessages.map((group, groupIndex) => (
                    <div key={groupIndex}>
                        <div className="text-center my-3">
                            <Badge color="secondary" pill className="px-3 py-1">
                                {group.date}
                            </Badge>
                        </div>
                        {group.messages.map((message) => (
                            <div
                                key={message.id}
                                className={`d-flex mb-2 ${message.isOwn ? 'justify-content-end' : 'justify-content-start'}`}
                                style={{ padding: '0 12px' }}
                            >
                                {!message.isOwn && (
                                    <div className="flex-shrink-0 me-2">
                                        {selectedConversation.avatar ? (
                                            <img
                                                src={selectedConversation.avatar}
                                                alt={selectedConversation.name}
                                                className="rounded-circle"
                                                style={{ width: '28px', height: '28px', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <i className="ri-user-circle-line text-secondary" style={{ fontSize: '28px' }}></i>
                                        )}
                                    </div>
                                )}
                                <div
                                    className={`p-2 rounded-3 ${message.isOwn
                                        ? 'bg-primary text-white'
                                        : 'bg-light'
                                        }`}
                                    style={{
                                        maxWidth: '70%',
                                        wordWrap: 'break-word'
                                    }}
                                >
                                    {!message.isOwn && (
                                        <div className="fw-semibold small mb-1">
                                            {message.sender_name || selectedConversation.name}
                                        </div>
                                    )}
                                    <div style={{ fontSize: '14px' }}>{message.text}</div>
                                    <div className="d-flex justify-content-end align-items-center gap-1 mt-1">
                                        <small className={`${message.isOwn ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '10px' }}>
                                            {formatMessageTime(message.timestamp)}
                                        </small>
                                        {message.isOwn && message.isRead && (
                                            <i className="ri-check-double-line" style={{ fontSize: '11px' }}></i>
                                        )}
                                        {message.isOwn && !message.isRead && (
                                            <i className="ri-check-line" style={{ fontSize: '11px' }}></i>
                                        )}
                                    </div>
                                </div>
                                {message.isOwn && (
                                    <div className="flex-shrink-0 ms-2">
                                        <i className="ri-user-star-line text-primary" style={{ fontSize: '28px' }}></i>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </>
        );
    };

    return (
        <>
            <div className="position-relative d-inline-block">
                <button
                    onClick={() => setOpen(!open)}
                    className="btn btn-ghost-secondary btn-icon rounded-circle position-relative"
                    style={{ width: '38px', height: '38px' }}
                    title="Messages"
                >
                    <i className='mdi mdi-chat-processing-outline fs-20'></i>
                </button>
                {totalUnreadCount > 0 && (
                    <Badge
                        color="danger"
                        pill
                        className="position-absolute top-0 start-100 translate-middle"
                        style={{ fontSize: '10px', cursor: 'pointer', zIndex: 1 }}
                    >
                        {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
                    </Badge>
                )}
            </div>

            <Offcanvas
                isOpen={open}
                toggle={() => setOpen(!open)}
                direction="end"
                className="offcanvas-end border-0"
                backdrop={false}
                scrollable={true}
                style={{ width: "400px" }}
                tabIndex={-1}
                id="theme-settings-offcanvas"
            >
                <OffcanvasHeader
                    className={`d-flex align-items-center bg-primary bg-gradient p-3 offcanvas-header-dark ${!selectedConversation ? 'justify-content-between' : ''}`}
                    toggle={() => setOpen(!open)}
                >
                    {!selectedConversation ? (
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <span className="m-0 text-white">Messages</span>
                            <div>
                                {totalUnreadCount > 0 && (
                                    <Badge color="warning" pill className="me-2">
                                        {totalUnreadCount} non lu(s)
                                    </Badge>
                                )}
                                <Button
                                    color="link"
                                    className="text-white p-0"
                                    onClick={openUsersModal}
                                    title="Nouvelle conversation"
                                    style={{ textDecoration: 'none' }}
                                >
                                    <i className="ri-add-line fs-20"></i>
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center w-100">
                            <button
                                className="btn btn-link text-white p-0 me-2 back-button"
                                onClick={backToList}
                                style={{ textDecoration: 'none' }}
                            >
                                <i className="ri-arrow-left-line fs-20"></i>
                            </button>
                            <span className="m-0 text-white flex-grow-1">{selectedConversation.name}</span>
                            {loadingMessages && (
                                <Spinner size="sm" color="light" className="me-2" />
                            )}
                        </div>
                    )}
                </OffcanvasHeader>

                <OffcanvasBody className="p-0 d-flex flex-column" style={{ height: "calc(100vh - 70px)" }}>
                    {!selectedConversation ? (
                        <>
                            <div className="p-3 border-bottom">
                                <InputGroup>
                                    <Input
                                        type="text"
                                        placeholder="Rechercher une conversation..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <Button color="primary">
                                        <i className="ri-search-line"></i>
                                    </Button>
                                </InputGroup>
                            </div>

                            <SimpleBar className="flex-grow-1">
                                {loading && conversations.length === 0 ? (
                                    <div className="text-center p-4">
                                        <Spinner color="primary" className="mb-2" />
                                        <p className="text-muted mt-2">Chargement des messages...</p>
                                    </div>
                                ) : filteredConversations.length === 0 ? (
                                    <div className="text-center p-4">
                                        <i className="ri-chat-1-line fs-1 text-muted mb-3 d-block"></i>
                                        <p className="text-muted mb-0">Aucune conversation trouvée</p>
                                        {!loading && (
                                            <Button
                                                color="primary"
                                                size="sm"
                                                className="mt-3"
                                                onClick={fetchConversations}
                                            >
                                                Rafraîchir
                                            </Button>
                                        )}
                                    </div>
                                ) : (
                                    filteredConversations.map(conversation => (
                                        <div
                                            key={conversation.id}
                                            className="d-flex align-items-center p-3 border-bottom conversation-item"
                                            onClick={(e) => openConversation(conversation, e)}
                                            style={{
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s',
                                                backgroundColor: conversation.unreadCount > 0 ? 'rgba(13, 110, 253, 0.1)' : 'transparent'
                                            }}
                                        >
                                            <div className="flex-shrink-0 position-relative">
                                                {conversation.avatar ? (
                                                    <img
                                                        src={conversation.avatar}
                                                        alt={conversation.name}
                                                        className="rounded-circle"
                                                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <i className="ri-user-circle-line fs-1 text-secondary"></i>
                                                )}
                                            </div>

                                            <div className="flex-grow-1 ms-3">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <h6 className="mb-0 fw-semibold">
                                                        {conversation.name}
                                                    </h6>
                                                    <small className="text-muted">{conversation.lastMessageTime}</small>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <small className={`text-truncate ${conversation.unreadCount > 0 ? 'fw-semibold text-dark' : 'text-muted'}`} style={{ maxWidth: '180px' }}>
                                                        {conversation.unreadCount > 0 && '✨ '}
                                                        {conversation.lastMessage}
                                                    </small>
                                                    {conversation.unreadCount > 0 && (
                                                        <Badge color="danger" pill>
                                                            {conversation.unreadCount}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </SimpleBar>
                        </>
                    ) : (
                        <>
                            <SimpleBar className="flex-grow-1 p-0 chat-messages">
                                <div
                                    className="d-flex flex-column"
                                    style={{
                                        minHeight: '100%',
                                        justifyContent: selectedConversation.messages?.length === 0 ? 'center' : 'flex-end',
                                        paddingTop: selectedConversation.messages?.length === 0 ? '0' : '16px'
                                    }}
                                >
                                    {!selectedConversation.messages || selectedConversation.messages.length === 0 ? (
                                        <div className="text-center p-4">
                                            {loadingMessages ? (
                                                <>
                                                    <Spinner color="primary" className="mb-2" />
                                                    <p className="text-muted">Chargement des messages...</p>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-chat-smile-line fs-1 text-muted mb-3 d-block"></i>
                                                    <p className="text-muted">Aucun message pour le moment</p>
                                                    <p className="text-muted small">Soyez le premier à envoyer un message !</p>
                                                </>
                                            )}
                                        </div>
                                    ) : (
                                        renderMessagesWithDates()
                                    )}
                                </div>
                            </SimpleBar>

                            <div className="p-3 border-top bg-light">
                                <div className="d-flex gap-2">
                                    <Input
                                        type="textarea"
                                        placeholder="Écrivez votre message..."
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                sendMessage();
                                            }
                                        }}
                                        style={{ resize: 'none' }}
                                        rows={1}
                                        disabled={sendingMessage}
                                    />
                                    <Button
                                        color="primary"
                                        onClick={sendMessage}
                                        disabled={!newMessage.trim() || sendingMessage}
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: '40px', height: '38px' }}
                                    >
                                        {sendingMessage ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <i className="ri-send-plane-fill"></i>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </OffcanvasBody>
            </Offcanvas>

            <Modal isOpen={showUsersModal} toggle={() => setShowUsersModal(false)} size="md">
                <ModalHeader toggle={() => setShowUsersModal(false)}>
                    Nouvelle conversation
                </ModalHeader>
                <ModalBody>
                    <div className="mb-3">
                        <Input
                            type="text"
                            placeholder="Rechercher un contact..."
                            value={usersSearchTerm}
                            onChange={(e) => setUsersSearchTerm(e.target.value)}
                        />
                    </div>

                    {usersLoading ? (
                        <div className="text-center p-4">
                            <Spinner color="primary" className="mb-2" />
                            <p className="text-muted mt-2">Chargement des contacts...</p>
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="text-center p-4">
                            <i className="ri-user-unfollow-line fs-1 text-muted mb-3 d-block"></i>
                            <p className="text-muted mb-0">Aucun contact trouvé</p>
                        </div>
                    ) : (
                        <ListGroup>
                            {filteredUsers.map((chatUser) => (
                                <ListGroupItem
                                    key={chatUser.id}
                                    action
                                    onClick={() => startNewConversation(chatUser)}
                                    style={{ cursor: 'pointer' , padding: '0.5rem 1rem'}}
                                    className="d-flex align-items-center justify-content-between py-2"
                                >
                                    <div className="flex-grow-1 d-flex align-items-center">
                                        <h6 className="mb-0">{chatUser.name}</h6>
                                        <StatusIndicator status={chatUser.status} isLoading={false} />
                                    </div>
                                    <i className="ri-chat-smile-line text-primary fs-4"></i>
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    )}
                </ModalBody>
            </Modal>

            <style>{`
                .conversation-item:hover {
                    background-color: rgba(0, 0, 0, 0.05) !important;
                }
                .chat-messages {
                    overflow-y: auto;
                }
                .chat-messages > div {
                    height: 100%;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .conversation-item {
                    animation: fadeIn 0.3s ease-out;
                }
                textarea { resize: none; }
                textarea:focus { box-shadow: none; }
                .list-group-item:hover {
                    background-color: rgba(13, 110, 253, 0.05);
                }
                @keyframes blink-green {
                    0%, 100% { opacity: 1; box-shadow: 0 0 4px rgba(16, 185, 129, 0.8); }
                    50%       { opacity: 0.6; box-shadow: 0 0 8px rgba(16, 185, 129, 1); }
                }
                .blink-green {
                    animation: blink-green 1.5s ease-in-out infinite;
                }
            `}</style>
        </>
    );
};

export default ChatSidebar;